import { RandomInt, rankOf, suitOf } from "../engine/cards.ts";
import { evaluate, categoryOf } from "../engine/evaluator.ts";
import type { Action, PokerTable } from "../engine/game.ts";

/** A deliberately simple opponent: plays sensibly, never exceptionally. Good for filling a table. */
export function botDecide(table: PokerTable, seat: number, rand: RandomInt): Action {
  const p = table.players[seat]!;
  const legal = table.legalActions(p.id)!;
  const toCall = legal.call ?? 0;
  const pot = table.players.reduce((s, q) => s + (q ? q.total : 0), 0);

  const strength = handStrength(p.hole, table.board); // 0..1
  const roll = rand(100) / 100;
  const bluff = roll > 0.93;

  const raiseTo = (frac: number): Action => {
    const r = legal.raise!;
    const unit = table.config.unit;
    const target = table.currentBet + Math.max(table.minRaise, Math.round((pot + toCall) * frac));
    let to = Math.round(target / unit) * unit;
    to = Math.min(r.max, Math.max(r.min, to));
    return to >= r.max ? { type: "allin" } : { type: "raise", amount: to };
  };
  const checkOrCall = (): Action => (legal.check ? { type: "check" } : { type: "call" });
  const checkOrFold = (): Action => (legal.check ? { type: "check" } : { type: "fold" });

  if (strength > 0.8 && legal.raise) return roll < 0.25 ? checkOrCall() : raiseTo(0.6 + roll * 0.6);
  if (strength > 0.55) {
    if (legal.raise && roll < 0.3) return raiseTo(0.5);
    return toCall <= pot * 0.6 + table.config.bigBlind ? checkOrCall() : checkOrFold();
  }
  if (strength > 0.35) {
    return toCall <= table.config.bigBlind * 2 || (toCall <= pot * 0.25 && roll < 0.6) ? checkOrCall() : checkOrFold();
  }
  if (bluff && legal.raise && toCall === 0) return raiseTo(0.5);
  if (toCall === 0) return { type: "check" };
  return roll < 0.1 && toCall <= table.config.bigBlind ? { type: "call" } : { type: "fold" };
}

/** Rough 0..1 strength. Preflop uses card ranks; later streets use the made hand. */
function handStrength(hole: number[], board: number[]): number {
  if (hole.length < 2) return 0;
  if (board.length < 3) {
    const [a, b] = hole.map(rankOf).sort((x, y) => y - x) as [number, number];
    const pair = a === b;
    const suited = suitOf(hole[0]!) === suitOf(hole[1]!);
    let s = (a + b) / 24 * 0.55 + (pair ? 0.35 + a / 12 * 0.15 : 0);
    if (suited) s += 0.05;
    if (!pair && a - b <= 2) s += 0.04;
    return Math.min(1, s);
  }
  const cat = categoryOf(evaluate([...hole, ...board]));
  return [0.15, 0.45, 0.7, 0.82, 0.88, 0.92, 0.96, 0.99, 1][cat]!;
}
